/**
 * NotificationDrawer - Right-side drawer for notifications in the Header
 *
 * Renders a right-anchored MUI Drawer that slides in from the right.
 * Shows up to 15 recent notifications with a "View All" footer link.
 * Positioned below the 64px header and overlays content.
 *
 * The only filter here is Unread Only, applied server-side via the `read` query
 * param. Product filtering lives on the full notifications page, not the drawer.
 */

import { Suspense, useState, useMemo } from "react";
import { FormattedMessage, IntlProvider, useIntl } from "react-intl";
import { ChevronRight } from "@opengov/react-capital-assets";
import { Box, Drawer, Typography, Skeleton, Button, FormControlLabel, Switch } from "@mui/material";
import type { Notification } from "@opengov/bnp-core";
import { useNotificationsQuery } from "@opengov/bnp-core";
import { NotificationDrawerItem } from "./NotificationDrawerItem";
import { EmptyState } from "../EmptyState/EmptyState";
import { styles } from "./NotificationsDrawer.styles";
import { DEFAULT_LOCALE } from "../NavBar/utils/intl";
import messages from "./locales/en-US.json";

const MAX_DRAWER_ITEMS = 15;

interface NotificationDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly entityId: string;
  readonly baseUrl: string;
  readonly onViewAllNotificationsClick?: () => void;
}

function DrawerItemsSkeleton() {
  return (
    <Box sx={styles.skeletonContainer}>
      {["skeleton-1", "skeleton-2", "skeleton-3"].map((id) => (
        <Box key={id} sx={styles.skeletonItem}>
          <Skeleton variant="text" width="70%" height={20} />
          <Skeleton variant="text" width="40%" height={16} />
        </Box>
      ))}
    </Box>
  );
}

function DrawerItems({
  notifications,
  baseUrl,
  entityId,
}: {
  readonly notifications: Notification[];
  readonly baseUrl: string;
  readonly entityId: string;
}) {
  if (notifications.length === 0) {
    return (
      <Box sx={styles.emptyStateContainer}>
        <EmptyState />
      </Box>
    );
  }

  return (
    <>
      {notifications.map((notification: Notification) => (
        <NotificationDrawerItem
          key={notification.id}
          notification={notification}
          baseUrl={baseUrl}
          entityId={entityId}
        />
      ))}
    </>
  );
}

function NotificationDrawerView({
  open,
  onClose,
  entityId,
  baseUrl,
  onViewAllNotificationsClick,
}: NotificationDrawerProps) {
  const intl = useIntl();
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);

  const queryParams = useMemo(
    () => ({
      read: unreadOnly ? "false" : undefined,
    }),
    [unreadOnly],
  );

  const queryResult = useNotificationsQuery(baseUrl, entityId, {
    params: queryParams,
  });

  const notifications = useMemo(() => {
    if (!queryResult.data) return [];
    return queryResult.data.pages.flatMap((p) => p.items).slice(0, MAX_DRAWER_ITEMS);
  }, [queryResult.data]);

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      data-testid="notification-bell-drawer"
      aria-labelledby="notification-drawer-title"
      sx={styles.drawer}
    >
      {/* Header - Pinned to top */}
      <Box sx={styles.header}>
        <Box sx={styles.headerRow}>
          <Typography id="notification-drawer-title" variant="h2">
            <FormattedMessage defaultMessage="Notifications" />
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={unreadOnly}
                onChange={(e) => {
                  setUnreadOnly(e.target.checked);
                }}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage defaultMessage="Unread Only" />
              </Typography>
            }
            labelPlacement="end"
            sx={styles.unreadToggle}
          />
        </Box>
      </Box>

      {/* Notification items - Scrollable middle section */}
      <Box sx={styles.itemsContainer}>
        <Suspense fallback={<DrawerItemsSkeleton />}>
          <DrawerItems notifications={notifications} baseUrl={baseUrl} entityId={entityId} />
        </Suspense>
      </Box>

      {/* Footer - Pinned to bottom */}
      <Box sx={styles.footer}>
        <Box sx={styles.footerRow}>
          <Button
            variant="text"
            onClick={() => {
              onViewAllNotificationsClick?.();
              onClose();
            }}
            endIcon={<ChevronRight aria-label={intl.formatMessage({ defaultMessage: "Chevron Right" })} />}
          >
            <FormattedMessage defaultMessage="View All" />
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

// Scoped provider: the drawer renders inside NavBar's IntlProvider, whose catalog doesn't include these messages.
export function NotificationDrawer(props: NotificationDrawerProps) {
  return (
    <IntlProvider locale={DEFAULT_LOCALE} messages={messages}>
      <NotificationDrawerView {...props} />
    </IntlProvider>
  );
}
